import { useState, useEffect } from 'react';
import { useDbUser } from '../hooks/useDbUser';
import { ProjectList } from '../components/projects/ProjectList';
import { Project, Matter, Goal, Task, Client, db } from '../lib/db';
import { toast } from 'sonner';

export function ProjectsPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState<Project[]>([]);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);


  const fetchData = async () => {
    if (!dbUserId) return;

    setIsLoading(true);
    try {
      const [fetchedProjects, fetchedMatters, fetchedGoals, fetchedTasks, fetchedClients] = await Promise.all([
        db.projects.findByUserId(dbUserId),
        db.matters.findByUserId(dbUserId),
        db.goals.findByUserId(dbUserId),
        db.tasks.findByUserId(dbUserId),
        db.clients.findByUserId(dbUserId),
      ]);
      setProjects(fetchedProjects);
      setMatters(fetchedMatters);
      // Filter goals by selected year
      const yearGoals = fetchedGoals.filter(goal => goal.year === selectedYear);
      setGoals(yearGoals);
      setTasks(fetchedTasks);
      setClients(fetchedClients);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      fetchData();
    }
  }, [dbUserId, dbUserLoading, selectedYear]);

  const handleProjectMarkComplete = async (project: Project) => {
    if (!dbUserId) return;
    
    try {
      const updatedProject = await db.projects.markComplete(project.id, dbUserId);
      setProjects(projects.map(p => p.id === project.id ? updatedProject : p));
      toast.success('Project marked as complete');
    } catch (error) {
      console.error('Error marking project as complete:', error);
      toast.error('Failed to mark project as complete');
    }
  };
  
  const handleProjectMarkIncomplete = async (project: Project) => {
    if (!dbUserId) return;
    
    try {
      const updatedProject = await db.projects.markIncomplete(project.id, dbUserId);
      setProjects(projects.map(p => p.id === project.id ? updatedProject : p));
      toast.success('Project marked as incomplete');
    } catch (error) {
      console.error('Error marking project as incomplete:', error);
      toast.error('Failed to mark project as incomplete');
    }
  };

  if (dbUserLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!dbUserId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Please log in to view projects.</div>
      </div>
    );
  }

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() + i
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
          Select Year
        </label>
        <select
          id="year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="block w-full md:w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {dbUserId && (
        <ProjectList
          projects={projects}
          matters={matters}
          goals={goals}
          tasks={tasks}
          clients={clients}
          userId={dbUserId}
          onUpdate={fetchData}
          onMarkComplete={handleProjectMarkComplete}
          onMarkIncomplete={handleProjectMarkIncomplete}
        />
      )}
    </div>
  );
}